/**
 * ManualCardEntry — issue #29 AC3, scoped to SIMULATION only (#522)
 *
 * The desk manual-entry surface for the simulation gateway (the
 * Switchback demo runbook pins this flow). On the SIMULATION gateway
 * the flow is complete end-to-end — the server-side
 * createManualPaymentIntent mints the Payment row COMPLETED and settles
 * the booking's open invoice — so the surface speaks professional
 * desk-payment language with a small muted "Simulation" badge as the
 * honest environment disclosure (DESIGN-PHILOSOPHY: Counter Test,
 * Principles 2, 3 and 5).
 *
 * Every REAL gateway takes a different path and never reaches the
 * confirm button here:
 *   - Authorize.net renders AcceptJsKeyedEntry instead (Payments.tsx
 *     gateway-gates the method picker, #522) — and its
 *     /manual/payment-intent arm answers a typed 501 regardless.
 *   - A live Stripe tenant gets the server's typed error surfaced in
 *     the error state (Stripe Elements was never wired here).
 * The old non-simulation "confirm" branch — which faked success with a
 * setTimeout and no gateway call — is DELETED, and a non-simulation 201
 * (contract drift) now lands in the error state rather than a form.
 */
import { useCallback, useState } from 'react';
import {
  adminPaymentsApi,
} from '../../lib/api.js';
import { makeIdempotencyKey } from '../../lib/idempotency.js';

type ManualCardEntryProps = {
  readonly apiUrl: string;
  readonly bookingId: string;
  readonly totalCents: number;
  readonly tipCents: number;
  readonly onSuccess: (paymentId: string) => void;
  readonly onCancel: () => void;
};

type FlowState =
  | { kind: 'idle' }
  | { kind: 'creating-intent' }
  // awaiting-card is reachable ONLY off a simulation intent (#522):
  // the non-simulation shape is unrepresentable here by construction.
  | { kind: 'awaiting-card'; clientSecret: string; paymentId: string }
  | { kind: 'confirming' }
  | { kind: 'succeeded'; paymentId: string }
  | { kind: 'error'; message: string };

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function ManualCardEntry({
  apiUrl,
  bookingId,
  totalCents,
  tipCents,
  onSuccess,
  onCancel,
}: ManualCardEntryProps): JSX.Element {
  const [state, setState] = useState<FlowState>({ kind: 'idle' });

  const handleStart = useCallback(async () => {
    setState({ kind: 'creating-intent' });
    const result = await adminPaymentsApi.createManualPaymentIntent(apiUrl, {
      bookingId,
      tipCents,
      idempotencyKey: makeIdempotencyKey('manual', bookingId),
    });
    if (result.error || !result.data) {
      setState({
        kind: 'error',
        message: result.error ?? 'Could not create payment intent',
      });
      return;
    }
    if (!result.data.simulation) {
      // Contract drift guard (#522): only the simulation gateway 201s
      // this endpoint. A non-simulation intent must never arm the
      // client-side confirm below — that path moves NO money and would
      // report a fake success.
      setState({
        kind: 'error',
        message:
          'Manual card entry is not available on this payment gateway.',
      });
      return;
    }
    setState({
      kind: 'awaiting-card',
      clientSecret: result.data.clientSecret,
      paymentId: result.data.paymentId,
    });
  }, [apiUrl, bookingId, tipCents]);

  const handleConfirm = useCallback(() => {
    if (state.kind !== 'awaiting-card') return;
    const { paymentId } = state;
    setState({ kind: 'confirming' });
    // In simulation mode the server-side createManualPaymentIntent
    // mints the Payment row COMPLETED (and settles the booking's open
    // invoice) — verified server behavior as of PR #248; there is no
    // confirm endpoint to call. This button is presentation-only.
    setTimeout(() => {
      setState({ kind: 'succeeded', paymentId });
      onSuccess(paymentId);
    }, 300);
  }, [state, onSuccess]);

  const totalWithTip = totalCents + tipCents;

  return (
    <section
      className="ferro-card"
      aria-label="Manual card entry"
      data-testid="manual-card-entry"
    >
      <header className="ferro-card__header">
        <h3 className="ferro-section__subtitle">Manual card entry</h3>
        <span
          className="ferro-badge ferro-badge--neutral"
          data-testid="manual-total"
        >
          {formatMoney(totalWithTip)}
        </span>
      </header>

      <p className="ferro-muted">
        Use this when you have a card in hand but no Reader to tap on.
      </p>

      {state.kind === 'idle' ? (
        <div data-testid="manual-idle">
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => void handleStart()}
            data-testid="manual-start-button"
          >
            Open secure card form
          </button>
        </div>
      ) : null}

      {state.kind === 'creating-intent' ? (
        <p className="ferro-muted" role="status" data-testid="manual-status">
          Creating payment intent…
        </p>
      ) : null}

      {state.kind === 'awaiting-card' ? (
        <div data-testid="manual-awaiting-card">
          <div
            className="ferro-form"
            aria-label="Secure card form (Stripe Elements)"
            data-testid="stripe-elements-mount"
          >
            <p>
              Ready to charge {formatMoney(totalWithTip)} to the
              customer&rsquo;s card.
            </p>
            <span
              className="ferro-badge ferro-badge--neutral"
              data-testid="manual-simulation-badge"
            >
              Simulation
            </span>
          </div>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={handleConfirm}
            data-testid="manual-confirm-button"
          >
            {`Charge ${formatMoney(totalWithTip)}`}
          </button>
        </div>
      ) : null}

      {state.kind === 'confirming' ? (
        <p className="ferro-muted" role="status" data-testid="manual-status">
          Confirming payment…
        </p>
      ) : null}

      {state.kind === 'succeeded' ? (
        <div
          className="ferro-form__success"
          role="status"
          data-testid="manual-success"
        >
          {/* No raw payment UUIDs on screen (DESIGN-PHILOSOPHY anti-pattern);
              "recorded" — this surface only ever writes the simulation
              ledger (#522). */}
          <p>{`Payment of ${formatMoney(totalWithTip)} recorded.`}</p>
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div
          className="ferro-dashboard__error"
          role="alert"
          data-testid="manual-error"
        >
          <p>{state.message}</p>
        </div>
      ) : null}

      <footer className="ferro-card__footer">
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={onCancel}
          disabled={state.kind === 'confirming'}
          data-testid="manual-cancel-button"
        >
          Back
        </button>
      </footer>
    </section>
  );
}
